import React, { useEffect, useRef } from "react";
import { useAppSelector, useAppDispatch } from "./hooks/redux";
import { setCssVariables, setTheme, type Theme } from "./store/slices/uiSlice";
import type { RootState } from "./store";

function extractFontFamily(fontValue: string): string {
  return fontValue.split(',').map(f => {
    if (f.trim().includes(' ')) {
      return `"${f.trim()}"`;
    }
    return f.trim();
  }).join(', ');
}
// Load a Google Font dynamically
const loadGoogleFont = (fontFamily: string) => {
  if (!fontFamily) return;

  const cssId = `google-font-${fontFamily.replace(/\s+/g, '-').toLowerCase()}`;

  // Check if already loaded
  if (document.getElementById(cssId)) {
    return;
  }

  // Create link element
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.id = cssId;
  link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(fontFamily)}:ital,wght@0,300;0,400;0,500;0,600;0,700;0,800;1,300;1,400;1,500;1,600;1,700;1,800&display=swap`;
  document.head.appendChild(link);
};


interface ThemeProviderProps {
  children: React.ReactNode;
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  const theme = useAppSelector((state: RootState) => state.ui.theme);
  const customVariables = useAppSelector((state: RootState) => state.ui.customVariables);
  const dispatch = useAppDispatch();

  // Emit current theme to parent container
  const emitThemeToParent = (currentTheme: string) => {
    if (window.parent !== window) {
      const message = {
        type: 'THEME_CHANGED',
        theme: currentTheme,
        source: 'app-theme-provider'
      };
      console.log('[ThemeProvider] window.parent.postMessage(', JSON.stringify(message), ')');
      window.parent.postMessage(message, '*');
    }
    window.dispatchEvent(new CustomEvent('themeChanged', {
      detail: { theme: currentTheme }
    }));
  };

  // Apply theme class
  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove("light", "dark");

    let appliedTheme: string;
    if (theme === "system") {
      const mediaQueryResult = typeof window.matchMedia === 'function' 
        ? window.matchMedia("(prefers-color-scheme: dark)") 
        : null;
      const systemTheme = mediaQueryResult?.matches ? "dark" : "light";
      root.classList.add(systemTheme);
      appliedTheme = systemTheme;
    } else {
      root.classList.add(theme);
      appliedTheme = theme;
    }

    emitThemeToParent(appliedTheme);

    if (theme === "system") {
      const mediaQuery = typeof window.matchMedia === 'function'
        ? window.matchMedia("(prefers-color-scheme: dark)")
        : null;
      if (mediaQuery) {
        const handleSystemThemeChange = (e: MediaQueryListEvent) => {
          const newSystemTheme = e.matches ? "dark" : "light";
          root.classList.remove("light", "dark");
          root.classList.add(newSystemTheme);
          emitThemeToParent(newSystemTheme);
        };
        mediaQuery.addEventListener("change", handleSystemThemeChange);
        return () => mediaQuery.removeEventListener("change", handleSystemThemeChange);
      }
    }
  }, [theme]);

  function applyCustomVariables(customVariables: Record<string, string>) {
    // Remove any existing dynamic style elements
    const existingStyles = document.querySelectorAll('style[data-theme-variables]');
    existingStyles.forEach(style => style.remove());

    // Create a style element to inject CSS variables
    const style = document.createElement('style');
    style.setAttribute('data-theme-variables', 'true');

    // Split variables into light and dark mode
    const lightVars: string[] = [];
    const darkVars: string[] = [];
    const fontsToLoad = new Set<string>();

    Object.entries(customVariables).forEach(([key, value]) => {
      const stringValue = String(value);

      // Check if this is a font variable and extract font family to load
      if (key === '--font-sans' || key === '--font-serif' || key === '--font-mono') {
        const fontStack = value.replace(/["']/g, '').split(',').map(f => f.trim());
        lightVars.push(`  ${key}: ${extractFontFamily(fontStack.join(', '))};`);
        fontStack.forEach(font => fontsToLoad.add(font));
        return
      }

      // Check if this is a dark mode variable (ends with -dark or has dark: prefix)
      if (key.endsWith('-dark')) {
        // Strip the -dark suffix for the actual variable name
        const baseKey = key.slice(0, -5);
        darkVars.push(`  ${baseKey}: ${stringValue};`);
      } else if (stringValue.includes('.dark:')) {
        // Handle the ".dark:" syntax in the value
        const parts = stringValue.split('.dark:');
        const lightValue = parts[0].trim();
        const darkValue = parts[1]?.trim();

        if (lightValue) {
          lightVars.push(`  ${key}: ${lightValue};`);
        }
        if (darkValue) {
          darkVars.push(`  ${key}: ${darkValue};`);
        }
      } else {
        // Regular variable (applies to both modes unless overridden)
        lightVars.push(`  ${key}: ${stringValue};`);
      }
    });

    // Load any Google Fonts that aren't already loaded
    fontsToLoad.forEach(font => loadGoogleFont(font));

    // Build the CSS content
    let cssContent = '';
    if (lightVars.length > 0) {
      cssContent += `:root {\n${lightVars.join('\n')}\n}\n`;
    }
    if (darkVars.length > 0) {
      cssContent += `.dark {\n${darkVars.join('\n')}\n}\n`;
    }

    style.textContent = cssContent;
    document.head.appendChild(style);

    if (window.parent !== window) {
      const message = {
        type: 'CUSTOM_VARIABLES_UPDATED',
        variables: customVariables,
        source: 'app-theme-provider'
      };
      console.log('[ThemeProvider] window.parent.postMessage(', JSON.stringify(message), ')');
      window.parent.postMessage(message, '*');
    }
  }



  // Apply custom CSS variables and load Google Fonts for font families
  useEffect(() => {
    if (Object.keys(customVariables).length > 0) {
      applyCustomVariables(customVariables)
    } else {
      const root = document.documentElement;
      const computedStyles = getComputedStyle(root);
      const fontsToLoad = new Set<string>();
      const fontFamilies = new Map<
        '--font-sans' | '--font-serif' | '--font-mono', string
      >();
      for (let i = 0; i < computedStyles.length; i++) {
        const prop = computedStyles[i];
        const value = computedStyles.getPropertyValue(prop).trim();
        if (prop === '--font-sans' || prop === '--font-serif' || prop === '--font-mono') {
          const fontStack = value.split(',').map(f => f.trim());
          fontStack.forEach(font => fontsToLoad.add(font));
          fontFamilies.set(prop, value);
        }
      }
      applyCustomVariables(Object.fromEntries(fontFamilies));

      // Load any Google Fonts found in the initial CSS
      fontsToLoad.forEach(font => loadGoogleFont(font));
    }
  }, [customVariables]);


  // Listen for postMessage theme updates
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {

      // Handle CSS variable updates
      if (event.data?.type === 'UPDATE_CSS_VARIABLES' && event.data?.variables) {
        dispatch(setCssVariables(event.data.variables));
      }

      // Handle theme mode changes
      if (event.data?.type === 'SET_THEME' && event.data?.theme) {
        const newTheme = event.data.theme as Theme;
        if (newTheme === 'light' || newTheme === 'dark' || newTheme === 'system') {
          dispatch(setTheme(newTheme));
        }
      }

      // Handle custom variable requests
      if (event.data?.request === 'get-custom-variables' && event.data?.id) {

        // customVariables is already flat with .dark: separators
        // Just send it directly to the parent
        const targetWindow = (event.source as WindowProxy) || window.parent;

        const response = {
          id: event.data.id,
          response: customVariables,
          source: '@@replay-nut'
        };

        console.log('[ThemeProvider] window.parent.postMessage(', JSON.stringify(response), ')');
        targetWindow.postMessage(response, '*');
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [dispatch, customVariables]);

  // Notify parent of initial state on mount and when variables/theme change
  useEffect(() => {
    if (window.parent !== window) {
      const mediaQueryResult = typeof window.matchMedia === 'function'
        ? window.matchMedia("(prefers-color-scheme: dark)")
        : null;
      const appliedTheme = theme === "system"
        ? (mediaQueryResult?.matches ? "dark" : "light")
        : theme;
      const message = {
        type: 'APP_READY',
        theme: appliedTheme,
        variables: customVariables,
        source: 'app-theme-provider'
      };
      console.log('[ThemeProvider] window.parent.postMessage(', JSON.stringify(message), ')');
      window.parent.postMessage(message, '*');
    }
  }, [theme, customVariables]);

  return <>{children}</>;
}

export default ThemeProvider;